import type { I18n, MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';

import type { LinkCommand } from '@/shared/commands';
import { underConstructionPath } from '@/shared/config';
import { can, type Permission } from '@/shared/screen';

export interface NavCondition {
  readonly rootTenancy?: boolean;
  readonly flag?: {
    readonly key: string;
    readonly equals?: unknown;
    readonly not?: unknown;
    readonly truthy?: boolean;
  };
}

export interface NavLeaf {
  readonly label: MessageDescriptor;
  readonly legacyPath: string;
  readonly permission?: Permission | readonly Permission[];
  readonly when?: NavCondition;
}

export interface NavGroup extends Omit<NavLeaf, 'legacyPath'> {
  readonly id: string;
  readonly icon: string;
  readonly legacyPath: string;
  readonly strict?: boolean;
  readonly children?: readonly NavLeaf[];
}

export const LEGACY_NAV: readonly NavGroup[] = [
  {
    id: 'dashboard',
    icon: 'layout-dashboard',
    label: msg`Dashboard`,
    legacyPath: '/dashboard',
    permission: can('CreditNote', 'Read'),
  },
  {
    id: 'bank-account',
    icon: 'landmark',
    label: msg`Contas Digitais`,
    legacyPath: '/contas-digitais',
    strict: true,
    permission: [
      can('BankAccount', 'Read'),
      can('BankAccountRequest', 'Read'),
      can('BankAccount', 'ManageAutomaticDebit'),
      can('BankAccount', 'ViewTransitoryAccount'),
      can('FrontWebSPI', 'Read'),
      can('PixMed', 'Read'),
    ],
    children: [
      {
        label: msg`Contas`,
        legacyPath: '/contas-digitais/contas',
        permission: [can('BankAccount', 'Read'), can('BankAccount', 'ViewTransitoryAccount')],
      },
      {
        label: msg`Solicitações`,
        legacyPath: '/contas-digitais/solicitacoes',
        permission: can('BankAccountRequest', 'Read'),
      },
      {
        label: msg`Split Automático`,
        legacyPath: '/contas-digitais/split-automatico',
        permission: can('BankAccount', 'ManageAutomaticDebit', { strict: true }),
        when: { flag: { key: 'FEATURE_FLAGS.AUTOMATIC_SPLIT', truthy: true } },
      },
      {
        label: msg`Gestão SPI`,
        legacyPath: '/contas-digitais/gestao-spi',
        permission: can('FrontWebSPI', 'Read'),
      },
      {
        label: msg`Pix Med Admin`,
        legacyPath: '/contas-digitais/pix-med-admin',
        permission: can('PixMed', 'Read'),
      },
    ],
  },
  {
    id: 'approvals',
    icon: 'badge-check',
    label: msg`Aprovações`,
    legacyPath: '/aprovacoes',
    permission: can('PendingApprovals', 'Read'),
    when: { rootTenancy: true },
  },
  {
    id: 'chatbot',
    icon: 'bot',
    label: msg`Chatbot`,
    legacyPath: '/chatbot',
    when: { rootTenancy: true },
  },
  {
    id: 'conciliation',
    icon: 'receipt-text',
    label: msg`Conciliação`,
    legacyPath: '/concilicao',
    when: { rootTenancy: true, flag: { key: 'CONCILIATION_MENU', equals: 1 } },
  },
  {
    id: 'natural-person',
    icon: 'user',
    label: msg`Pessoas Físicas`,
    legacyPath: '/pessoas-fisicas',
    permission: can('NaturalPerson', 'Read'),
    when: { flag: { key: 'NATURAL_PERSON_MENU', not: 0 } },
    children: [
      { label: msg`Todas`, legacyPath: '/pessoas-fisicas/todas' },
      {
        label: msg`Pendentes`,
        legacyPath: '/pessoas-fisicas/pendentes',
        permission: can('NaturalPerson', 'ComplianceApproval'),
      },
    ],
  },
  {
    id: 'legal-person',
    icon: 'building-2',
    label: msg`Pessoas Jurídicas`,
    legacyPath: '/pessoas-juridicas',
    permission: can('LegalPerson', 'Read'),
    when: { flag: { key: 'LEGAL_PERSON_MENU', not: 0 } },
    children: [
      { label: msg`Todas`, legacyPath: '/pessoas-juridicas/todas' },
      {
        label: msg`Pendentes`,
        legacyPath: '/pessoas-juridicas/pendentes',
        permission: can('LegalPerson', 'ComplianceApproval'),
      },
    ],
  },
  {
    id: 'credit-note',
    icon: 'file-text',
    label: msg`Crédito`,
    legacyPath: '/ccb',
    permission: can('CreditNote', 'Read'),
    children: [
      { label: msg`Operações`, legacyPath: '/ccb/operacoes' },
      {
        label: msg`Contratar`,
        legacyPath: '/ccb/contratar',
        when: { flag: { key: 'ENABLE_NEW_SIMULATION', equals: 1 } },
      },
      {
        label: msg`Desconto`,
        legacyPath: '/ccb/desconto',
        when: { flag: { key: 'DESCONTO_MENU', equals: 1 } },
      },
      {
        label: msg`Cessões`,
        legacyPath: '/ccb/cessoes',
        permission: can('BatchAssignment', 'Read'),
      },
      { label: msg`Simular`, legacyPath: '/ccb/simular' },
      {
        label: msg`Gestão de autorizações`,
        legacyPath: '/ccb/gestao-autorizacoes',
        when: { flag: { key: 'ENABLE_AUTHORIZATION_MANAGEMENT', not: 0 } },
      },
      { label: msg`Excluídos`, legacyPath: '/ccb/excluidos' },
      {
        label: msg`Criar CPF teste`,
        legacyPath: '/ccb/criar-cpf-teste',
        when: { flag: { key: 'ENABLE_DATAPREV_TEST', equals: 1 } },
      },
      {
        label: msg`Excluir Aut. CPF teste`,
        legacyPath: '/ccb/excluir-autorizacao',
        when: { flag: { key: 'ENABLE_DATAPREV_TEST', equals: 1 } },
      },
    ],
  },
  {
    id: 'wallet-management',
    icon: 'wallet',
    label: msg`Gestão de carteira`,
    legacyPath: '/gestao-carteira',
    permission: can('BillingAccount', 'Read'),
    children: [
      { label: msg`Dashboard`, legacyPath: '/gestao-carteira/dashboard' },
      { label: msg`Carteiras`, legacyPath: '/gestao-carteira/carteiras' },
      { label: msg`Cobranças`, legacyPath: '/gestao-carteira/cobrancas' },
      { label: msg`Parcelamentos`, legacyPath: '/gestao-carteira/parcelamentos' },
      { label: msg`Régua de comunicação`, legacyPath: '/gestao-carteira/regua' },
      { label: msg`Régua de cobrança`, legacyPath: '/gestao-carteira/parametros' },
      {
        label: msg`Execução de garantias`,
        legacyPath: '/gestao-carteira/execucao-garantias',
        permission: can('NaturalPerson', 'AuctionQuery', { strict: true }),
      },
      {
        label: msg`Conciliação`,
        legacyPath: '/gestao-carteira/conciliacao',
        permission: can('Conciliation', 'Read'),
        when: { rootTenancy: true },
      },
      {
        label: msg`Recuperação de crédito`,
        legacyPath: '/gestao-carteira/recuperacao',
        permission: can('CreditRecovery', 'Read'),
      },
      {
        label: msg`Acordos`,
        legacyPath: '/gestao-carteira/recuperacao/acordos',
        permission: can('CreditRecovery', 'Read'),
      },
    ],
  },
  {
    id: 'register',
    icon: 'folder-cog',
    label: msg`Cadastro`,
    legacyPath: '/cadastro',
    permission: [
      can('CreditProduct', 'Read'),
      can('BankAccountProduct', 'Read'),
      can('Fund', 'Read'),
      can('CreditProductRules', 'Read'),
      can('BillingProduct', 'Read'),
    ],
    children: [
      {
        label: msg`Produtos de Conta`,
        legacyPath: '/cadastro/produtos-conta',
        permission: can('BankAccountProduct', 'Read'),
        when: { flag: { key: 'BANK_ACCOUNT_PRODUCT_MENU', not: 0 } },
      },
      {
        label: msg`Produtos de Crédito`,
        legacyPath: '/cadastro/produtos-credito',
        permission: can('CreditProduct', 'Read'),
        when: { flag: { key: 'CREDIT_PRODUCT_MENU', not: 0 } },
      },
      {
        label: msg`Produtos em Liquidação Automática`,
        legacyPath: '/cadastro/produtos-liquidacao-automatica',
        permission: can('CreditProduct', 'ManageAutomaticDebit', { strict: true }),
        when: { rootTenancy: true },
      },
      {
        label: msg`Central de Regras`,
        legacyPath: '/cadastro/configuracao-regras',
        permission: can('CreditProductRules', 'Read'),
      },
      {
        label: msg`Regras Dinâmicas`,
        legacyPath: '/cadastro/regras-dinamicas',
        when: { flag: { key: 'FEATURE_FLAGS.RULES_MENU', equals: 'DYNAMIC' } },
      },
      {
        label: msg`Produtos de Cobrança`,
        legacyPath: '/cadastro/gestao',
        permission: can('BillingProduct', 'Read'),
        when: { rootTenancy: true },
      },
      { label: msg`Convênios de Crédito`, legacyPath: '/cadastro/convenios' },
      {
        label: msg`Cessionários`,
        legacyPath: '/cadastro/fundos',
        permission: can('Fund', 'Read'),
        when: { flag: { key: 'FUND_MENU', not: 0 } },
      },
    ],
  },
  {
    id: 'partners',
    icon: 'handshake',
    label: msg`Parceiros`,
    legacyPath: '/parceiros',
    permission: can('TenantRequest', 'Read'),
    when: { rootTenancy: true },
    children: [{ label: msg`Solicitações`, legacyPath: '/parceiros/solicitacoes' }],
  },
  {
    id: 'security',
    icon: 'shield-check',
    label: msg`Segurança`,
    legacyPath: '/seguranca',
    permission: [can('User', 'Read'), can('UserGroup', 'Read')],
    children: [
      { label: msg`Usuários`, legacyPath: '/seguranca/usuarios', permission: can('User', 'Read') },
      { label: msg`Grupos`, legacyPath: '/seguranca/grupos', permission: can('UserGroup', 'Read') },
      {
        label: msg`Usuários de Aplicação`,
        legacyPath: '/seguranca/usuarios-aplicacao',
        permission: can('UserApplication', 'Read'),
        when: { rootTenancy: true },
      },
      // P5 (R3): "Servidor de Recursos" (`/seguranca/escopos-oauth`) saiu: o menu do legado sai das rotas
      // (SideMenu.tsx:132, `appRouter.routes`), e o master não tem a rota (router.tsx:1941-2052, e7f4d7b25).
      {
        label: msg`Permissões por Correspondente`,
        legacyPath: '/seguranca/recursos-correspondente',
        permission: can('TenantRequest', 'Read'),
        when: { rootTenancy: true },
      },
    ],
  },
];

export interface NavEnv {
  readonly can: (permission: Permission) => boolean;
  readonly isRootTenancy: boolean;
  readonly flag: (key: string) => unknown;
  readonly migrated: (legacyPath: string) => string | null;
  readonly canAccessPath?: (path: string) => boolean;
}

export interface ResolvedNavItem {
  readonly id: string;
  readonly label: MessageDescriptor;
  readonly icon?: string;
  readonly href: string;
  /** Ainda não migrou: o link abre a tela "Em construção" da casca (U15). */
  readonly underConstruction: boolean;
  readonly children: readonly ResolvedNavItem[];
}

function isPermissionList(
  value: Permission | readonly Permission[],
): value is readonly Permission[] {
  return Array.isArray(value);
}

type AccessEnv = Pick<NavEnv, 'can' | 'isRootTenancy' | 'flag'>;

/** `strict` do grupo é o `strictPermission` do handle legado (SideMenu.tsx:56): vale para toda a lista. */
function allowed(permission: NavLeaf['permission'], env: AccessEnv, strict?: boolean): boolean {
  if (!permission) return true;
  const list: readonly Permission[] = isPermissionList(permission) ? permission : [permission];
  return list.some((perm) =>
    env.can(strict && perm.strict === undefined ? { ...perm, strict: true } : perm),
  );
}

function passes(when: NavCondition | undefined, env: AccessEnv): boolean {
  if (!when) return true;
  if (when.rootTenancy && !env.isRootTenancy) return false;
  if (when.flag) {
    const value = env.flag(when.flag.key);
    if (when.flag.truthy && !value) return false;
    if ('equals' in when.flag && value !== when.flag.equals) return false;
    if ('not' in when.flag && value === when.flag.not) return false;
  }
  return true;
}

function link(legacyPath: string, env: NavEnv) {
  const target = env.migrated(legacyPath);
  return target
    ? { href: target, underConstruction: false }
    : { href: underConstructionPath(legacyPath), underConstruction: true };
}

export function resolveNavigation(tree: readonly NavGroup[], env: NavEnv): ResolvedNavItem[] {
  const out: ResolvedNavItem[] = [];
  for (const group of tree) {
    if (
      !allowed(group.permission, env, group.strict) ||
      !passes(group.when, env) ||
      (!group.children && env.canAccessPath?.(group.legacyPath) === false)
    )
      continue;
    const children = (group.children ?? [])
      .filter(
        (c) =>
          allowed(c.permission, env) &&
          passes(c.when, env) &&
          env.canAccessPath?.(c.legacyPath) !== false,
      )
      .map((c, i) => ({
        id: `${group.id}.${String(i)}`,
        label: c.label,
        ...link(c.legacyPath, env),
        children: [],
      }));
    if (group.children && children.length === 0) continue;
    const own = link(group.legacyPath, env);
    out.push({
      id: group.id,
      label: group.label,
      icon: group.icon,
      href: own.href,
      underConstruction: children.length > 0 ? children.every((c) => c.underConstruction) : own.underConstruction,
      children,
    });
  }
  return out;
}

/**
 * Regra da seção para a guarda, a paleta e o agente: o `RoutePermissionGuard` do legado confere o
 * handle da rota-mãe além do da folha (`RoutePermissionGuard.tsx:14-50`), com permissão,
 * `strictPermission` e tenant raiz; a flag do grupo é a condição que monta a rota (`router.tsx`).
 * Seção desconhecida não tem regra.
 */
export function sectionAccess(tree: readonly NavGroup[], env: AccessEnv): (sectionId: string) => boolean {
  return (sectionId) => {
    const group = tree.find((g) => g.id === sectionId);
    return !group || (allowed(group.permission, env, group.strict) && passes(group.when, env));
  };
}

/** Item ativo: só rota migrada casa com a URL; item em construção nunca fica ativo. */
export function isActive(item: ResolvedNavItem, path: string): boolean {
  if (item.children.length > 0) return item.children.some((c) => isActive(c, path));
  if (item.underConstruction) return false;
  return item.href === '/' ? path === '/' : path === item.href || path.startsWith(`${item.href}/`);
}

export function activeGroupId(items: readonly ResolvedNavItem[], path: string): string | null {
  return items.find((g) => g.children.length > 0 && isActive(g, path))?.id ?? null;
}

export interface NavLeafEntry {
  readonly item: ResolvedNavItem;
  readonly group: ResolvedNavItem | undefined;
}

export function navLeaves(items: readonly ResolvedNavItem[]): NavLeafEntry[] {
  return items.flatMap((g): NavLeafEntry[] =>
    g.children.length === 0
      ? [{ item: g, group: undefined }]
      : g.children.map((c) => ({ item: c, group: g })),
  );
}

/** Toda folha da navegação vira destino no ⌘K, com o grupo como dica e o ícone do grupo. */
export function navLinkCommands(items: readonly ResolvedNavItem[], i18n: I18n): LinkCommand[] {
  return navLeaves(items).map(({ item, group }) => {
    const icon = item.icon ?? group?.icon;
    return {
      id: item.id,
      label: i18n._(item.label),
      ...(group ? { hint: i18n._(group.label) } : {}),
      ...(icon ? { icon } : {}),
      href: item.href,
      external: false,
    };
  });
}

/** Início é a única tela nativa fora do menu do legado; entra no topo da navegação. */
export const HOME_ITEM: ResolvedNavItem = {
  id: 'home',
  label: msg`Início`,
  icon: 'house',
  href: '/',
  underConstruction: false,
  children: [],
};

export function withHome(items: readonly ResolvedNavItem[]): ResolvedNavItem[] {
  return [HOME_ITEM, ...items];
}

/** Onde um caminho do legado mora no menu: a seção e, se houver, a página. */
export interface NavArea {
  readonly section: NavGroup;
  readonly page: NavLeaf | undefined;
  /** O caminho é o da própria página ou seção, não uma tela dentro dela. */
  readonly exact: boolean;
}

function within(path: string, base: string): boolean {
  return path === base || path.startsWith(`${base}/`);
}

/** O item do menu cujo `legacyPath` é o prefixo mais longo do caminho (sem query nem âncora). */
export function navAreaOf(tree: readonly NavGroup[], legacyPath: string): NavArea | null {
  const path = (legacyPath.split(/[?#]/)[0] ?? '').replace(/\/+$/, '') || '/';
  const candidates = tree.flatMap((section) => [
    { section, page: undefined, base: section.legacyPath },
    ...(section.children ?? []).map((page) => ({ section, page, base: page.legacyPath })),
  ]);
  const match = candidates
    .filter((c) => within(path, c.base))
    .reduce<(typeof candidates)[number] | null>((best, c) => (!best || c.base.length > best.base.length ? c : best), null);
  return match ? { section: match.section, page: match.page, exact: path === match.base } : null;
}
